import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import Footer from '../components/Footer';
import { UI_CATEGORIES } from '../lib/supplierDashboardConstants';
import { normalizeDisplayCurrency } from '../lib/displayCurrency';
import { createManagedRequest } from '../lib/createManagedRequest';
import useRequestAttachments from '../hooks/useRequestAttachments';
import Notice from '../components/Notice';
import RequestReceived from '../components/RequestReceived';
import { requestReceivedCopy } from '../lib/requestReceivedCopy';
import { parseStoredPhone } from '../components/PhoneField';
import VerifiedPhoneField, { requestPhoneRefusal, phoneRefusalMessage } from '../components/VerifiedPhoneField';
import { saveProfilePhoneIfBlank } from '../lib/profilePhone';
import {
  ATTACH_ACCEPT, DELIVERY_PREFERENCES, REQUEST_UNITS, SAMPLE_REQUIREMENTS, labelFor, deliveryHintFor,
} from '../lib/requestFormOptions';
import CityPicker from '../components/CityPicker';
import { waLink } from '../lib/maabarContact';
import { sb } from '../supabase';
import RequestFeeNotice from '../components/RequestFeeNotice';
import { fetchFeeConfig, payFeeIfRequired } from '../lib/requestFee';
import { isPlainQuantity, normalizeQuantityInput, quantityMessage } from '../lib/requestQuantity';
import { requestContactNote } from '../lib/requestContactNote';
import useServiceTracking, { anyFilled } from '../hooks/useServiceTracking';

const DRAFT_KEY = 'maabar_wizard_draft';
const DESC_MAX = 1000;

const EMPTY_FORM = {
  // quantity + unit + sample_requirement: REQUIRED and NOT preselected
  // (2026-09-27). This form never collected quantity or unit, so every /request
  // row was written with both null (M-10092), and it saved a hidden
  // sample_requirement of 'preferred' the trader never chose. unit starts ''
  // — a visible 'piece' default would still save a choice nobody made.
  title_ar: '', title_en: '', description: '', quantity: '', unit: '',
  category: '', budget_per_unit: '', budget_currency: 'SAR',
  // payment_plan '' — no deposit preselected (2026-09-28); required below.
  payment_plan: '', sample_requirement: '', phone: '',
  // ADDED 2026-09-21 (migration 20260921122508). A managed request needs a
  // destination and a delivery ask exactly as much as a direct one does —
  // Maabar sources it, but the factory still has to price freight to a place.
  ship_city: '', delivery_preference: '',
};

const T = {
  ar: {
    pageTitle: 'طلب مُدار',
    intro: 'نستلم طلبك بالكامل ونتولّى البحث والتواصل والتوصيل حتى باب بيتك.',
    stages: ['تفاصيل الطلب', 'المتطلبات', 'الموازنة والدفع', 'المتابعة والتسليم'],
    videoStrong: 'نصوّر لك المنتج بالفيديو قبل الشحن', videoRest: 'لتتأكد بنفسك قبل أن يصلك.',
    titleLabel: 'عنوان الطلب', titleHint: 'عنوان مختصر يوضّح ما تريد',
    titlePlaceholder: 'مثال: كراسي مكتب دوّارة',
    catLabel: 'ما الذي تبحث عنه؟', catHint: 'اختر الفئة الأقرب لطلبك', catPlaceholder: 'اختر الفئة',
    descLabel: 'وصف طلبك بالتفصيل', descHint: 'كلما كان الوصف أدق، كانت النتائج أفضل',
    descPlaceholder: 'اكتب تفاصيل طلبك، المواصفات، الكمية، الجودة المطلوبة، الاستخدام…',
    budgetLabel: 'ميزانيتك التقديرية للوحدة (اختياري)', budgetHint: 'يساعدنا على البحث ضمن النطاق المناسب لك',
    budgetPlaceholder: 'أدخل ميزانيتك التقديرية',
    paymentLabel: 'الدفعة المقدمة', paymentHint: 'اختر نسبة الدفعة للبدء بالبحث',
    cityLabel: 'مدينة التسليم', cityHint: 'وين تبي البضاعة توصل', cityPlaceholder: 'اختر المدينة',
    deliveryLabel: 'كيف تبي تستلم البضاعة؟', deliveryHint: 'يحدّد ما الذي يشمله سعر المورّد',
    errCity: 'يرجى اختيار مدينة التسليم.', errDelivery: 'يرجى اختيار طريقة الاستلام.',
    qtyLabel: 'الكمية المطلوبة', qtyHint: 'العدد فقط، والوحدة من القائمة', qtyPlaceholder: 'مثال: 500', unitPlaceholder: 'اختر الوحدة',
    sampleLabel: 'متطلبات العينة', sampleHint: 'هل تحتاج عينة قبل الطلب الكامل؟',
    errPayment: 'يرجى اختيار الدفعة المقدمة.',
    errQty: 'يرجى إدخال الكمية المطلوبة.', errUnit: 'يرجى اختيار وحدة الكمية.', errSample: 'يرجى اختيار متطلبات العينة.',
    phoneLabel: 'رقم الجوال (واتساب)', phoneHint: 'للتواصل ومتابعة طلبك',
    draft: 'حفظ كمسودة', draftSaved: 'تم حفظ المسودة', submit: 'متابعة', submitting: 'جارٍ الإرسال…',
    security: 'بياناتك آمنة ولن تتم مشاركتها مع أي طرف ثالث',
    signinNote: 'سيُطلب منك تسجيل الدخول عند إرسال الطلب.',
    errTitle: 'يرجى كتابة عنوان للطلب.', errDesc: 'يرجى وصف طلبك بالتفصيل.', errPhone: 'يرجى توثيق رقم جوالك عبر واتساب قبل إرسال الطلب.',
    errGeneric: 'حدث خطأ أثناء إرسال الطلب، حاول مرة أخرى.',
    attachLabel: 'المرفقات (اختياري)',
    attachHint: 'صور، PDF، ZIP — حتى 6 ملفات، 10 ميجابايت لكل ملف.',
    attachPick: 'اختر ملفات',
    attachRemove: 'حذف',
    draftFilesNote: 'استعدنا مسودتك. إن كنت قد أضفت ملفات ولا تظهر هنا، أضفها مرة أخرى.',
    draftFilesBack: (n) => `استعدنا مسودتك مع مرفقاتك (${n}).`,
    warnUpload: (n) => `أُرسل طلبك، لكن تعذّر رفع: ${n}. يمكنك إضافتها لاحقاً من صفحة الطلب.`,
    payments: [
      { val: '30', label: '30% مقدماً', hint: 'خيار شائع' },
      { val: '100', label: '100% مقدماً' },
    ],
    successNote: 'يمكنك متابعة مراحل طلبك في أي وقت من لوحتك.',
    successBtn: 'متابعة الطلب',
    successWa: 'راسلنا على واتساب الآن',
    successRef: 'رقم طلبك',
  },
  en: {
    pageTitle: 'Managed order',
    intro: 'We take your request end to end — sourcing, negotiation, and delivery to your door.',
    stages: ['Request details', 'Requirements', 'Budget & payment', 'Follow-up & delivery'],
    videoStrong: 'We send you a video of your product before shipping', videoRest: 'so you can check it yourself before it arrives.',
    titleLabel: 'Request title', titleHint: 'A short title of what you need',
    titlePlaceholder: 'e.g. Swivel office chairs',
    catLabel: 'What are you looking for?', catHint: 'Pick the closest category', catPlaceholder: 'Choose a category',
    descLabel: 'Describe your request in detail', descHint: 'The more precise the description, the better the results',
    descPlaceholder: 'Write your specs, quantity, required quality, intended use…',
    budgetLabel: 'Estimated budget per unit (optional)', budgetHint: 'Helps us search within the right range for you',
    budgetPlaceholder: 'Enter your estimated budget',
    paymentLabel: 'Upfront deposit', paymentHint: 'Choose the deposit to start sourcing',
    cityLabel: 'Delivery city', cityHint: 'Where the goods should arrive', cityPlaceholder: 'Select a city',
    deliveryLabel: 'How do you want it delivered?', deliveryHint: "Decides what the supplier's price covers",
    errCity: 'Please choose the delivery city.', errDelivery: 'Please choose how you want it delivered.',
    qtyLabel: 'Quantity', qtyHint: 'Numbers only — pick the unit from the list', qtyPlaceholder: 'e.g. 500', unitPlaceholder: 'Choose unit',
    sampleLabel: 'Sample requirement', sampleHint: 'Do you need a sample before the full order?',
    errPayment: 'Please choose the upfront deposit.',
    errQty: 'Please enter the required quantity.', errUnit: 'Please choose a quantity unit.', errSample: 'Please select sample requirements.',
    phoneLabel: 'Mobile number (WhatsApp)', phoneHint: 'To reach you and follow up on your order',
    draft: 'Save as draft', draftSaved: 'Draft saved', submit: 'Continue', submitting: 'Submitting…',
    security: 'Your data is secure and never shared with any third party',
    signinNote: "You'll be asked to sign in when submitting.",
    errTitle: 'Please enter a request title.', errDesc: 'Please describe your request.', errPhone: 'Please verify your mobile number via WhatsApp before sending the request.',
    errGeneric: 'Something went wrong while submitting, please try again.',
    attachLabel: 'Attachments (optional)',
    attachHint: 'Images, PDF, ZIP — up to 6 files, 10 MB each.',
    attachPick: 'Choose files',
    attachRemove: 'Remove',
    draftFilesNote: 'Your draft was restored. If you had added files and they are not shown here, please add them again.',
    draftFilesBack: (n) => `Your draft was restored with its attachments (${n}).`,
    warnUpload: (n) => `Your request was sent, but these did not upload: ${n}. You can add them later from the request page.`,
    payments: [
      { val: '30', label: '30% upfront', hint: 'Popular choice' },
      { val: '100', label: '100% upfront' },
    ],
    successNote: 'You can track your order stages anytime from your dashboard.',
    successBtn: 'View request',
    successWa: 'Message us on WhatsApp',
    successRef: 'Your order no.',
  },
  zh: {
    pageTitle: '托管订单',
    intro: '我们全程负责您的需求——寻源、谈判、直至送货上门。',
    stages: ['需求详情', '要求', '预算与付款', '跟进与交付'],
    videoStrong: '发货前我们会为您拍摄产品视频', videoRest: '让您在收货前亲自确认。',
    titleLabel: '需求标题', titleHint: '简短说明您想要什么',
    titlePlaceholder: '例如：办公转椅',
    catLabel: '您在找什么？', catHint: '选择最接近的类别', catPlaceholder: '选择类别',
    descLabel: '详细描述您的需求', descHint: '描述越精确，结果越好',
    descPlaceholder: '写下规格、数量、所需质量、用途…',
    budgetLabel: '每单位预算（可选）', budgetHint: '帮助我们在合适的范围内寻源',
    budgetPlaceholder: '输入您的预估预算',
    paymentLabel: '预付定金', paymentHint: '选择开始寻源的定金比例',
    cityLabel: '收货城市', cityHint: '货物送达的城市', cityPlaceholder: '选择城市',
    deliveryLabel: '您希望如何收货？', deliveryHint: '这决定了供应商报价包含哪些费用',
    errCity: '请选择收货城市。', errDelivery: '请选择收货方式。',
    qtyLabel: '所需数量', qtyHint: '只填数字，单位从列表中选择', qtyPlaceholder: '例：500', unitPlaceholder: '选择单位',
    sampleLabel: '样品要求', sampleHint: '下完整订单前是否需要样品？',
    errPayment: '请选择预付定金比例。',
    errQty: '请输入所需数量。', errUnit: '请选择数量单位。', errSample: '请选择样品要求。',
    phoneLabel: '手机号码（WhatsApp）', phoneHint: '用于联系并跟进您的订单',
    draft: '保存草稿', draftSaved: '草稿已保存', submit: '继续', submitting: '提交中…',
    security: '您的数据安全，绝不会分享给任何第三方',
    signinNote: '提交时会要求您登录。',
    errTitle: '请输入需求标题。', errDesc: '请描述您的需求。', errPhone: '提交需求前，请先通过 WhatsApp 验证您的手机号码。',
    errGeneric: '提交时出错，请重试。',
    attachLabel: '附件（可选）',
    attachHint: '图片、PDF、ZIP — 最多 6 个文件，每个 10 MB。',
    attachPick: '选择文件',
    attachRemove: '删除',
    draftFilesNote: '已恢复您的草稿。如果您之前添加过文件但这里没有显示，请重新添加。',
    draftFilesBack: (n) => `已恢复您的草稿及附件（${n} 个）。`,
    warnUpload: (n) => `需求已发送，但以下文件未能上传：${n}。您可以稍后在需求页面补充。`,
    payments: [
      { val: '30', label: '预付 30%', hint: '常见选择' },
      { val: '100', label: '预付 100%' },
    ],
    successNote: '您可随时在仪表板中跟踪订单阶段。',
    successBtn: '查看需求',
    successWa: '通过 WhatsApp 联系我们',
    successRef: '订单号',
  },
};

// Field icons (rounded-square badge in each card) — simple line glyphs.
const svg = (d, extra) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{d}{extra}</svg>
);
const ICONS = {
  tag: svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-6.6-6.6A2 2 0 0 1 3.4 12.6V5.4A2 2 0 0 1 5.4 3.4h7.2a2 2 0 0 1 1.4.6l6.6 6.6a2 2 0 0 1 0 2.8z" /><circle cx="7.8" cy="7.8" r="1.2" /></>),
  grid: svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  pencil: svg(<path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />),
  wallet: svg(<><path d="M3 7a2 2 0 0 1 2-2h12v3M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6" /><circle cx="16.5" cy="13" r="1.3" /></>),
  shield: svg(<path d="M12 3l8 3v5.5c0 4.7-3.3 7.6-8 8.9-4.7-1.3-8-4.2-8-8.9V6z" />),
  wa: svg(<path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.2-5.3A8.5 8.5 0 1 1 21 11.5z" />),
};

function FieldCard({ icon, label, hint, isAr, children }) {
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '15px 16px 17px', marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', color: 'var(--text-secondary)' }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', ...font }}>{label}</div>
          {hint ? <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.6, ...font }}>{hint}</div> : null}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

export default function RequestWizard({ lang = 'ar', user, profile, setProfile, displayCurrency }) {
  const nav = useNavigate();
  const location = useLocation();
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  usePageTitle('requests', lang);
  const viewerCurrency = normalizeDisplayCurrency(displayCurrency || 'SAR');
  const cats = (UI_CATEGORIES[lang] || UI_CATEGORIES.ar).filter((c) => c.val !== 'all');
  const titleKey = isAr ? 'title_ar' : 'title_en';

  const params = new URLSearchParams(location.search);
  const prefillCategory = params.get('category') || params.get('sector') || '';
  const prefillTitle = params.get('prefillTitle') || '';
  const factoryId = params.get('factory') || '';

  const [form, setForm] = useState(() => {
    let base = { ...EMPTY_FORM, budget_currency: viewerCurrency };
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) {
        const saved = JSON.parse(draft);
        // A draft saved before 2026-09-27 has no `unit` key and carries the old
        // hidden sample_requirement default ('preferred') — a value the trader
        // never chose. Drop it; they pick it on this form like everyone else.
        // Same for its old hidden payment_plan default ('30').
        if (!Object.prototype.hasOwnProperty.call(saved, 'unit')) {
          delete saved.sample_requirement;
          delete saved.payment_plan;
        }
        base = { ...base, ...saved };
      }
    } catch { /* ignore */ }
    if (prefillCategory) base.category = prefillCategory;
    if (prefillTitle && !base[titleKey]) base[titleKey] = prefillTitle;
    // A draft saved before 2026-09-24 holds the phone as typed ("05…"); bring
    // it to E.164 so PhoneField can show it and the validity check can read it.
    base.phone = parseStoredPhone(base.phone).e164;
    return base;
  });
  // The phone must be VERIFIED by a WhatsApp code (owner, 2026-10-01) — the
  // server refuses a managed request otherwise (enforce_verified_request_phone).
  // form.phone keeps whatever is typed, for the draft; verifiedPhone is the
  // number VerifiedPhoneField has confirmed, or '' — and only it is sent.
  // The prefill (account phone, else profiles.phone) is the field's job.
  const [verifiedPhone, setVerifiedPhone] = useState('');
  // { hint, n } when the insert came back phone_required / phone_not_verified.
  const [phoneRefusal, setPhoneRefusal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const [toast, setToast] = useState('');
  // The request fee (299 SAR). Shown before submit; charged right after the
  // insert — the server decides whether this request carries it.
  const [feeConfig, setFeeConfig] = useState(null);
  const [feePending, setFeePending] = useState(false);
  useEffect(() => { fetchFeeConfig(sb).then(setFeeConfig); }, []);
  // Growth OS (step 7): this form's journey. Field names only, never what was typed.
  const svc = useServiceTracking('managed');
  useEffect(() => { if (feeConfig?.enabled && feeConfig.amount) svc.feeShown(); }, [feeConfig, svc]);

  // persistKey: the attached files survive the sign-in redirect with the draft
  // (owner, 2026-10-17) — see src/lib/draftFiles.js.
  const picker = useRequestAttachments(lang, { persistKey: 'request-wizard' });
  // Filenames that did not upload. Non-empty means the request was published
  // ANYWAY and the trader is being told what to re-add.
  const [failedFiles, setFailedFiles] = useState([]);

  // Whether this session started from a restored draft.
  //
  // The draft is JSON in sessionStorage, and a File cannot be serialized — so
  // every typed field comes back and the attachments silently do not. Without
  // saying so, a trader who attached a spec sheet, signed in, and returned would
  // see a complete-looking form and send the request without it. Read once in an
  // initializer: it describes how this mount started and must not flip later.
  const [draftRestored] = useState(() => {
    try { return Boolean(sessionStorage.getItem(DRAFT_KEY)); } catch { return false; }
  });

  // Persist the draft so it survives the sign-in redirect.
  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (anyFilled(form, ['budget_currency'])) svc.start(); }, [form, svc]);
  const titleVal = form[titleKey] || '';
  const descLen = String(form.description || '').length;

  function saveDraft() {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    setToast(t.draftSaved);
    setTimeout(() => setToast(''), 1800);
  }

  async function handleSubmit() {
    if (!titleVal.trim()) { svc.fieldError('title'); setError(t.errTitle); return; }
    if (!String(form.description).trim()) { svc.fieldError('description'); setError(t.errDesc); return; }
    if (!String(form.quantity || '').trim()) { svc.fieldError('quantity'); setError(t.errQty); return; }
    // A plain positive number only (owner, 2026-09-30) — payment multiplies
    // price × quantity. Anything else is refused here, never stripped into a
    // number the trader did not type.
    if (!isPlainQuantity(form.quantity)) { svc.fieldError('quantity'); setError(quantityMessage(lang)); return; }
    if (!form.unit) { svc.fieldError('unit'); setError(t.errUnit); return; }
    if (!form.payment_plan) { svc.fieldError('payment_plan'); setError(t.errPayment); return; }
    if (!form.sample_requirement) { svc.fieldError('sample_requirement'); setError(t.errSample); return; }
    if (!String(form.ship_city).trim()) { svc.fieldError('ship_city'); setError(t.errCity); return; }
    if (!form.delivery_preference) { svc.fieldError('delivery_preference'); setError(t.errDelivery); return; }
    if (!user) { nav('/login/buyer'); return; }   // draft already in sessionStorage
    // After the sign-in check: a visitor cannot verify a number yet.
    if (!verifiedPhone) { svc.fieldError('phone'); setError(t.errPhone); return; }

    setSubmitting(true);
    setError('');

    // Upload BEFORE the insert, so the paths go in with the row rather than as
    // a follow-up update that could fail on its own. uploadAll never throws: a
    // file that does not make it is reported, the request is created regardless,
    // and the trader is told what to re-add (decision §4.1).
    const { paths: attachmentPaths, failed } = await picker.uploadAll(user.id);

    const payload = {
      ...form,
      category: form.category || 'other',
      // KSA-only city list on this form, so the country is a constant here.
      // createManagedRequest does NOT default it — that function also serves
      // the factory-quote modal, which covers the GCC and has its own picker.
      ship_country: 'Saudi Arabia',
      // The verified number, never the raw field (owner, 2026-10-01).
      phone: verifiedPhone,
      attachment_paths: attachmentPaths,
      attachments_missing: failed.length > 0,
    };
    const { request, error: submitErr } = await createManagedRequest({ user, form: payload, lang, viewerCurrency });
    setSubmitting(false);
    if (submitErr || !request?.id) {
      // The server's verified-phone rule: back to the phone step with the
      // matching message, not the generic error.
      const hint = requestPhoneRefusal(submitErr);
      if (hint) {
        setPhoneRefusal((r) => ({ hint, n: (r?.n || 0) + 1 }));
        setError(phoneRefusalMessage(hint, lang));
        svc.fieldError('phone_server');
        return;
      }
      setError(t.errGeneric);
      return;
    }
    svc.submitted({ request_id: request.id, category: payload.category, has_attachments: attachmentPaths.length > 0, fee_required: !!feeConfig?.enabled });
    // First phone this trader has given us → their profile, so the next
    // request prefills. Blank profiles only; never overwrites. Fire-and-forget.
    saveProfilePhoneIfBlank({ userId: user.id, profile, e164: verifiedPhone })
      .then((saved) => { if (saved) setProfile?.((p) => (p ? { ...p, phone: verifiedPhone } : p)); });
    // Set before setSubmitted so the success screen renders with it in place.
    setFailedFiles(failed.map((f) => f.name));

    try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    picker.clearPersisted();

    // The request fee: straight to Telr's page. The request is saved already;
    // nothing downstream acts on it until the fee is paid, and the factory
    // email (if any) goes from the return page once it is.
    try {
      setSubmitting(true);
      if (await payFeeIfRequired(sb, request.id, { factoryId })) return;   // leaving for Telr
      setSubmitting(false);
    } catch (feeErr) {
      console.error('[RequestWizard] could not start the request fee payment:', feeErr?.message || feeErr);
      setSubmitting(false);
      setFeePending(true);
      setSubmitted(request);
      return;
    }

    // No fee on this request: the factory email is best-effort — fired in the
    // background so the success screen appears the moment the request is created.
    if (factoryId) {
      sb.functions.invoke('factory-request-email', { body: { requestId: request.id, factoryId } }).catch(() => { /* best-effort */ });
    }
    // ═══ REMOVED 2026-09-24 — the client-side admin_new_request alert ═══════
    // The ops alert is sent by the DATABASE now: trg_admin_notify_new_managed_
    // request (migration 20260924122135) fires on the insert for request_kind
    // managed / idea_to_product, so it survives a closed tab and covers every
    // managed path, not just this page. send-email rejects the type from
    // clients (SERVER_ONLY_TYPES), so a call here would only 401.
    // ⚠️ DO NOT REINTRODUCE — it would be dead on arrival.
    // ══════════════════════════════════════════════════════════════════════════
    setSubmitted(request);
  }

  const dir = isAr ? 'rtl' : 'ltr';
  const arc = isAr ? ' ar' : '';
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };

  return (
    <div className="full-page" dir={dir}>
      <div className="fx-wrap fx-wrap-narrow">
        <h1 className={`fx-h1${arc}`}>{t.pageTitle}</h1>
        <p className={`fx-sub${arc}`}>{t.intro}</p>

        {submitted ? (
          /* ── Professional confirmation ── */
          <div className="fx-panel" style={{ textAlign: 'center', padding: '46px 28px' }}>
            <div style={{ width: 70, height: 70, borderRadius: '50%', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', border: '1.5px solid rgba(139,115,85,0.32)' }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#8B7355" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </div>
            <h2 className={`fx-h1${arc}`} style={{ fontSize: 26, margin: '0 0 10px' }}>{requestReceivedCopy(lang).title}</h2>
            {/* On the SUCCESS panel and toned warn, because the request WAS
                created. The reference number above is real; only some files
                are missing, and the trader can add them from the request page. */}
            {failedFiles.length > 0 && (
              <Notice tone="warn" isAr={isAr} style={{ textAlign: 'start' }}>
                {t.warnUpload(failedFiles.join(isAr ? '، ' : ', '))}
              </Notice>
            )}
            <p style={{ ...font, fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px' }}>
              {t.successRef}: <b style={{ color: 'var(--text-primary)', letterSpacing: '0.04em' }}>{submitted.request_ref || submitted.id}</b>
            </p>
            {feePending && (
              <Notice tone="warn" isAr={isAr} style={{ textAlign: 'start' }}>
                {isAr ? 'طلبك محفوظ، لكن لم نتمكن من فتح صفحة دفع رسوم تأكيد الطلب. أكمل الدفع من «طلباتي» ليبدأ فريق مَعبر العمل عليه.'
                  : "Your request is saved, but we couldn't open the fee payment page. Complete the request confirmation fee from My requests so the Maabar team can start."}
              </Notice>
            )}
            {/* Replaced the old "an account manager contacts you on WhatsApp
                within a few hours" line — owner, 2026-10-03: no confirmation
                promises faster than 3–7 working days. */}
            <RequestReceived lang={lang} showTitle={false} style={{ margin: '0 auto 8px', maxWidth: 470 }} />
            <p style={{ ...font, fontSize: 12.5, color: 'var(--text-muted)', margin: '0 auto 26px', maxWidth: 420 }}>{t.successNote}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={waLink(`استفسار عن طلبي المُدار رقم ${submitted.request_ref || submitted.id}`)} target="_blank" rel="noopener noreferrer"
                style={{ ...font, display: 'inline-flex', alignItems: 'center', gap: 8, background: '#25D366', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 22px', fontSize: 14, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.8 14.01c-.24.68-1.42 1.3-1.95 1.34-.5.04-.5.4-3.15-.66-2.67-1.05-4.35-3.76-4.48-3.94-.13-.18-1.07-1.42-1.07-2.71 0-1.29.68-1.92.92-2.19.24-.26.52-.33.7-.33.17 0 .35 0 .5.01.16.01.38-.06.59.45.24.58.81 2 .88 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.56.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.37.27.14.43.12.59-.07.16-.19.68-.79.86-1.06.18-.27.36-.22.6-.13.24.09 1.53.72 1.8.85.27.13.44.2.5.31.07.11.07.63-.17 1.31z" /></svg>
                {t.successWa}
              </a>
              <button type="button" onClick={() => nav('/managed-order/' + submitted.id)}
                style={{ ...font, background: 'none', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 22px', fontSize: 14, color: 'var(--text-primary)', cursor: 'pointer' }}>{t.successBtn}</button>
            </div>
          </div>
        ) : (
          <>
            {/* ── Managed-order journey stepper (stage 1 is this form; 2–4 are handled by Maabar).
                 flexDirection stays 'row' so dir=rtl lays step 1 on the RIGHT. ── */}
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginBottom: 26 }}>
              {t.stages.map((label, i) => (
                <React.Fragment key={label}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: '0 0 auto', width: 74 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, background: i === 0 ? '#1a1814' : 'var(--bg-subtle)', color: i === 0 ? '#fff' : 'var(--text-muted)', border: i === 0 ? 'none' : '1px solid var(--border)' }}>{i + 1}</div>
                    <span style={{ ...font, fontSize: 10, textAlign: 'center', lineHeight: 1.35, color: i === 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
                  </div>
                  {i < t.stages.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)', marginTop: 15 }} />}
                </React.Fragment>
              ))}
            </div>

            {/* Reassurance the buyer should know up front */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'rgba(45,106,79,0.06)', border: '1px solid rgba(45,106,79,0.22)', borderRadius: 14, padding: '12px 14px', marginBottom: 14 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2D6A4F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}><rect x="2" y="6" width="14" height="12" rx="2" /><path d="M16 10l6-3v10l-6-3z" /></svg>
              <p style={{ ...font, fontSize: 13, lineHeight: 1.6, margin: 0, color: 'var(--text-primary)' }}><strong>{t.videoStrong}</strong> — {t.videoRest}</p>
            </div>

            {/* Title (buyer writes it) */}
            <FieldCard icon={ICONS.tag} label={t.titleLabel} hint={t.titleHint} isAr={isAr}>
              <input className="form-input" value={titleVal} onChange={(e) => setField(titleKey, e.target.value)}
                dir={isAr ? 'rtl' : 'ltr'} placeholder={t.titlePlaceholder} maxLength={120} />
            </FieldCard>

            {/* Category */}
            <FieldCard icon={ICONS.grid} label={t.catLabel} hint={t.catHint} isAr={isAr}>
              <select className="form-input" value={form.category} onChange={(e) => setField('category', e.target.value)} style={font}>
                <option value="" disabled>{t.catPlaceholder}</option>
                {cats.map((c) => (<option key={c.val} value={c.val}>{c.label}</option>))}
              </select>
            </FieldCard>

            {/* Description + counter */}
            <FieldCard icon={ICONS.pencil} label={t.descLabel} hint={t.descHint} isAr={isAr}>
              <textarea className="form-input" rows={4} maxLength={DESC_MAX} style={{ resize: 'vertical', ...font }}
                value={form.description} onChange={(e) => setField('description', e.target.value)}
                dir={isAr ? 'rtl' : 'ltr'} placeholder={t.descPlaceholder} />
              <p style={{ ...font, fontSize: 11, color: 'var(--text-muted)', textAlign: isAr ? 'left' : 'right', margin: '4px 2px 0' }}>{descLen} / {DESC_MAX}</p>
              <p style={{ ...font, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.7, margin: '4px 2px 0' }}>{requestContactNote(lang)}</p>
            </FieldCard>

            {/* Budget per unit (optional) — input then currency, so dir=rtl puts SAR on the left */}
            {/* Quantity: a NUMBER plus a UNIT, as on the direct form — a plain
                number in the box (the submit refuses anything else with
                QUANTITY_MESSAGE; it is no longer stripped as typed, which made
                «100 إلى 150» into 100150), the unit from the same REQUEST_UNITS
                list. No unit preselected: the trader picks one or the submit
                says so. */}
            <FieldCard icon={ICONS.grid} label={`${t.qtyLabel} *`} hint={t.qtyHint} isAr={isAr}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input" inputMode="decimal" value={form.quantity}
                  onChange={(e) => setField('quantity', normalizeQuantityInput(e.target.value))}
                  placeholder={t.qtyPlaceholder} dir="ltr" style={{ flex: 1 }} />
                <select className="form-input" value={form.unit || ''}
                  onChange={(e) => setField('unit', e.target.value)}
                  style={{ width: 140, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  <option value="">{t.unitPlaceholder}</option>
                  {REQUEST_UNITS.map((u) => (<option key={u.key} value={u.key}>{labelFor(u, lang)}</option>))}
                </select>
              </div>
            </FieldCard>

            <FieldCard icon={ICONS.wallet} label={t.budgetLabel} hint={t.budgetHint} isAr={isAr}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input" type="number" min="0" value={form.budget_per_unit}
                  onChange={(e) => setField('budget_per_unit', e.target.value)} placeholder={t.budgetPlaceholder} dir={isAr ? 'rtl' : 'ltr'} style={{ flex: 1 }} />
                {/* SAR only (owner, 2026-09-29): a trader never works in USD/CNY. */}
                <span className="form-input" style={{ width: 92, direction: 'ltr', fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>SAR</span>
              </div>
            </FieldCard>

            {/* Destination + delivery ask. Same two fields, same wording and
                same stored values as the direct form in Requests.jsx — one
                question must not be asked two ways depending on which of our
                own forms the trader happened to open. */}
            <FieldCard icon={ICONS.grid} label={`${t.cityLabel} *`} hint={t.cityHint} isAr={isAr}>
              {/* Required; searchable, every city or typed (CityPicker, 2026-10-25).
                  A listed city stores its English name: it travels to a factory. */}
              <CityPicker value={form.ship_city} onChange={(v) => setField('ship_city', v)} lang={lang} />
            </FieldCard>

            <FieldCard icon={ICONS.shield} label={`${t.deliveryLabel} *`} hint={t.deliveryHint} isAr={isAr}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DELIVERY_PREFERENCES.map((d) => {
                  const on = form.delivery_preference === d.key;
                  return (
                    <label key={d.key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 'var(--radius-md)',
                      border: '1px solid', borderColor: on ? 'var(--border-muted)' : 'var(--border-subtle)',
                      background: on ? 'var(--bg-raised)' : 'transparent',
                      fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                    }}>
                      <input type="radio" name="wizard_delivery" value={d.key} checked={on}
                        onChange={() => setField('delivery_preference', d.key)}
                        style={{ accentColor: 'var(--text-secondary)', cursor: 'pointer', marginTop: 3 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>{labelFor(d, lang)}</span>
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-disabled)', marginTop: 2, lineHeight: 1.6 }}>
                          {deliveryHintFor(d, lang)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </FieldCard>

            {/* Upfront deposit (30% / 100%) */}
            <FieldCard icon={ICONS.shield} label={`${t.paymentLabel} *`} hint={t.paymentHint} isAr={isAr}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {t.payments.map((p) => {
                  const on = form.payment_plan === p.val;
                  return (
                    <button key={p.val} type="button" onClick={() => setField('payment_plan', p.val)}
                      style={{ flex: '1 1 140px', textAlign: isAr ? 'right' : 'left', padding: '11px 14px', borderRadius: 'var(--radius-control)', cursor: 'pointer', ...font,
                        border: on ? '1px solid #1a1814' : '1px solid var(--border)', background: on ? '#1a1814' : 'var(--bg-base)', color: on ? '#fff' : 'var(--text-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{p.label}</span>
                        {p.hint && <span style={{ fontSize: 11, opacity: on ? 0.85 : 0.6 }}>{p.hint}</span>}
                      </span>
                      {on && <span style={{ fontSize: 13, flex: '0 0 auto' }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </FieldCard>

            {/* Sample requirement — the three values of SAMPLE_REQUIREMENTS,
                REQUIRED, nothing preselected (never save a choice the trader
                did not make). Same pill style as the deposit card above. */}
            <FieldCard icon={ICONS.shield} label={`${t.sampleLabel} *`} hint={t.sampleHint} isAr={isAr}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SAMPLE_REQUIREMENTS.map((sr) => {
                  const on = form.sample_requirement === sr.key;
                  return (
                    <button key={sr.key} type="button" onClick={() => setField('sample_requirement', sr.key)}
                      style={{ flex: '1 1 140px', textAlign: isAr ? 'right' : 'left', padding: '11px 14px', borderRadius: 'var(--radius-control)', cursor: 'pointer', ...font,
                        border: on ? '1px solid #1a1814' : '1px solid var(--border)', background: on ? '#1a1814' : 'var(--bg-base)', color: on ? '#fff' : 'var(--text-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{labelFor(sr, lang)}</span>
                      {on && <span style={{ fontSize: 13, flex: '0 0 auto' }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </FieldCard>

            {/* Attachments — optional throughout. Nothing here can block the
                submit: a file over the limit is refused at pick time, and a file
                that fails to upload is reported after the request is created. */}
            <FieldCard icon={ICONS.wa} label={t.attachLabel} hint={t.attachHint} isAr={isAr}>
              {/* The draft's files come back from IndexedDB (src/lib/draftFiles.js,
                  owner 2026-10-17). Say so when they did; when none did (a
                  browser without IndexedDB, or older than 24 h), the old
                  warning still applies — a form that looks complete without
                  its files is the trap. */}
              {draftRestored && picker.hydrated && picker.restoredCount > 0 && (
                <Notice tone="ok" isAr={isAr}>{t.draftFilesBack(picker.restoredCount)}</Notice>
              )}
              {draftRestored && picker.hydrated && picker.files.length === 0 && (
                <Notice tone="warn" isAr={isAr}>{t.draftFilesNote}</Notice>
              )}

              <label className={`fx-btn-ghost${arc}`} style={{ display: 'inline-block', cursor: 'pointer', marginTop: 8 }}>
                {t.attachPick}
                <input type="file" multiple accept={ATTACH_ACCEPT} style={{ display: 'none' }}
                  onChange={(e) => { picker.addFiles(e.target.files); e.target.value = ''; }} />
              </label>

              {picker.files.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
                  {picker.files.map((f) => (
                    <li key={f.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                      flexDirection: isAr ? 'row-reverse' : 'row', ...font, fontSize: 13,
                    }}>
                      <span dir="ltr" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <button type="button" onClick={() => picker.removeFile(f.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a05050', fontSize: 12, ...font }}>
                        {t.attachRemove}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Pick-time validation: too many files, or one over 10 MB.
                  An error, not a warning — nothing was sent and it is fixable. */}
              {picker.error && <Notice tone="error" isAr={isAr}>{picker.error}</Notice>}
            </FieldCard>

            {/* WhatsApp phone */}
            <FieldCard icon={ICONS.wa} label={t.phoneLabel} hint={t.phoneHint} isAr={isAr}>
              <VerifiedPhoneField
                value={form.phone || profile?.phone || ''}
                onVerified={setVerifiedPhone}
                onNumberChange={(e164) => setField('phone', e164)}
                lang={lang}
                profile={profile}
                setProfile={setProfile}
                refusal={phoneRefusal}
                inputClassName="form-input"
                selectClassName="form-input"
                selectStyle={{ width: 'auto' }}
                inputStyle={{ minWidth: 0 }}
              />
            </FieldCard>

            <RequestFeeNotice lang={lang} config={feeConfig} style={{ marginTop: 14 }} />

            {!!error && <Notice tone="error" isAr={isAr}>{error}</Notice>}
            {!user && <p style={{ ...font, fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>{t.signinNote}</p>}

            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexDirection: isAr ? 'row-reverse' : 'row' }}>
              <button type="button" className={`fx-btn-ghost${arc}`} onClick={saveDraft}>{t.draft}</button>
              <button type="button" className={`fx-btn-primary${arc}`} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={handleSubmit} disabled={submitting || (Boolean(user) && !verifiedPhone)}>
                {submitting ? t.submitting : (<>{t.submit}<span aria-hidden style={{ fontSize: 17, lineHeight: 1 }}>{isAr ? '←' : '→'}</span></>)}
              </button>
            </div>

            <p style={{ ...font, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 16 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              {t.security}
            </p>
          </>
        )}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, insetInlineStart: '50%', transform: 'translateX(-50%)', background: '#1a1814', color: '#fff', padding: '10px 20px', borderRadius: 999, fontSize: 13, ...font, zIndex: 50 }}>{toast}</div>
      )}

      <Footer lang={lang} />
    </div>
  );
}
